import React from 'react';
import { Link } from 'react-router-dom';

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <img
              src="/HRO-Logo-transparent-white-logo.png"
              alt="Horse Racing Oracle"
              className="h-12"
            />
          </Link>
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Terms & Conditions
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-600">
                By using Horse Racing Oracle's website, products, and services ("Products"), you agree to be bound by these Terms and Conditions 
                ("Terms") and our Privacy Policy. If you do not agree to these Terms, please do not use our Products.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Definitions</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Parties</h3>
                  <p className="text-gray-600">
                    "You" and "your" refer to you, as a user of our Products. "We," "us," and "our" refer to Horse Racing Oracle.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Products</h3>
                  <p className="text-gray-600">
                    Our "Products" include our automated horse race betting bot, website, and all related services.
                  </p>
                </div>
              </div>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  You may access or use our Products only if you:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Are at least 18 years old</li>
                  <li>Are legally permitted to use gambling services in your jurisdiction</li>
                  <li>Have the authority to enter into these Terms</li>
                  <li>Are not a competitor or using our Products for competitive analysis</li>
                  <li>Have not been previously banned from our Products</li>
                </ul>
              </div>
            </section>

            {/* Account Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Terms</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  To use our Products, you must:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Create an account with accurate information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Promptly notify us of any unauthorised access</li>
                  <li>Take responsibility for all activities under your account</li>
                  <li>Not create multiple accounts or transfer your account to others</li>
                </ul>
              </div>
            </section>

            {/* Web App Usage */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Web Application Usage</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  When using our web application, including the staking calculator and betting history tracker, you acknowledge and agree that:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Past performance does not guarantee future results</li>
                  <li>You are responsible for setting appropriate risk limits when calculating your stakes</li>
                  <li>We do not place bets on your behalf</li>
                  <li>We are not responsible for any losses incurred</li>
                  <li>Technical issues may affect the web application performance</li>
                </ul>
              </div>
            </section>

            {/* Payment and Subscription */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Payment and Subscription</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  By subscribing to our Products:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>You agree to pay all applicable fees</li>
                  <li>Subscriptions auto-renew unless cancelled</li>
                  <li>Refunds are subject to our Refund Policy</li>
                  <li>We may modify pricing with notice</li>
                  <li>Trial periods are limited to one per user</li>
                </ul>
              </div>
            </section>

            {/* Restrictions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Restrictions</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  You agree not to:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Reverse engineer our Products</li>
                  <li>Attempt to access unauthorised areas</li>
                  <li>Use our Products for illegal activities</li>
                  <li>Share your account credentials</li>
                  <li>Interfere with our services</li>
                  <li>Circumvent any limitations</li>
                </ul>
              </div>
            </section>

            {/* Disclaimer */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimer</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Our Products are provided "AS IS" without warranty. We do not guarantee:
                </p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Specific betting outcomes or profits</li>
                  <li>Continuous, uninterrupted service</li>
                  <li>Accuracy of race analysis</li>
                </ul>
                <p className="text-gray-600 font-medium mt-4">
                  Gambling involves significant risk of loss. Never bet more than you can afford to lose.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-600">
                To the maximum extent permitted by law, Horse Racing Oracle shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, 
                resulting from your use of our Products.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-600">
                We may modify these Terms at any time. Continued use of our Products after changes constitutes 
                acceptance of the modified Terms. We will notify you of material changes via email or through our Products.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-600">
                For questions about these Terms, please contact us through our{' '}
                <Link to="/system/contact" className="text-primary-600 hover:text-primary-500">
                  support page
                </Link>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
} 